import React, { useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Modal } from "bootstrap/dist/js/bootstrap.bundle.min.js";
import { toast } from "react-toastify";
import { useTranslation } from "../i18n/I18nContext";
import { sendNotification } from "../services/notification";
import { getSucursalInfo } from "../services/auth";

// Replica o Modal "Notificar" de sge_infantil_web/src/pages/student/mantain/index.js:
// tipo Convocatória (data/hora da reunião + até 4 pontos de agenda + notas) ou
// Comunicado (mensagem livre), com pré-visualização da mensagem final antes do envio.
const buildInitialFields = (defaultType = "Convocatoria") => ({
  type: defaultType,
  meetingDate: "",
  meetingTime: "",
  agenda1: "",
  agenda2: "",
  agenda3: "",
  agenda4: "",
  notes: "",
  comunicado: "",
});

const formatDatePt = (isoDate) => {
  if (!isoDate) return "";
  const date = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(date.getTime())) return isoDate;
  return date.toLocaleDateString("pt-PT", { day: "numeric", month: "long", year: "numeric" });
};

const NotifyModal = ({ modalId, pairs, count, onSent, modalRefOverride, defaultType = "Convocatoria" }) => {
  const { t } = useTranslation();
  const [fields, setFields] = useState(() => buildInitialFields(defaultType));
  const [sending, setSending] = useState(false);
  const localRef = useRef(null);
  const modalRef = modalRefOverride || localRef;

  const setField = (field) => (e) => {
    const value = e.target.value;
    setFields((f) => ({ ...f, [field]: value }));
  };

  useEffect(() => {
    const el = modalRef.current;
    if (!el) return undefined;
    const handleHidden = () => setFields(buildInitialFields(defaultType));
    el.addEventListener("hidden.bs.modal", handleHidden);
    return () => el.removeEventListener("hidden.bs.modal", handleHidden);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalRef.current, defaultType]);

  const sucursalName = getSucursalInfo()?.description || getSucursalInfo()?.name || "";

  const preview = useMemo(() => {
    if (fields.type === "Convocatoria") {
      const agendaLines = [fields.agenda1, fields.agenda2, fields.agenda3, fields.agenda4]
        .filter((a) => a.trim() !== "")
        .map((a, idx) => `${idx + 1}. ${a}`)
        .join("\n");
      return t("estudante.notify.preview.convocatoria", {
        sucursal: sucursalName,
        date: formatDatePt(fields.meetingDate) || "-",
        time: fields.meetingTime || "-",
        agenda: agendaLines || "-",
        notes: fields.notes || "-",
      });
    }
    return t("estudante.notify.preview.comunicado", {
      sucursal: sucursalName,
      message: fields.comunicado || "",
    });
  }, [fields, sucursalName, t]);

  const validate = () => {
    if (fields.type === "Convocatoria") {
      if (!fields.meetingDate || !fields.meetingTime || !fields.agenda1.trim()) {
        toast.error(t("validation.required"));
        return false;
      }
      return true;
    }
    if (!fields.comunicado.trim()) {
      toast.error(t("validation.required"));
      return false;
    }
    return true;
  };

  const handleConfirm = async () => {
    if (!validate()) return;
    if (!pairs || pairs.length === 0) {
      toast.error(t("notificacoes.error.noRecipients"));
      return;
    }
    setSending(true);
    try {
      const payload = {
        type: fields.type,
        idContactPairs: pairs,
        message: preview,
      };
      if (fields.type === "Convocatoria") {
        payload.param1 = sucursalName;
        payload.param2 = formatDatePt(fields.meetingDate);
        payload.param3 = fields.meetingTime;
        payload.agenda1 = fields.agenda1;
        payload.agenda2 = fields.agenda2;
        payload.agenda3 = fields.agenda3;
        payload.agenda4 = fields.agenda4;
        payload.notes = fields.notes.trim() || "-";
      } else {
        payload.param1 = sucursalName;
        payload.param2 = fields.comunicado;
      }

      await sendNotification(payload);
      toast.success(t("estudante.notify.success"));
      onSent?.();
      Modal.getInstance(modalRef.current)?.hide();
    } catch (err) {
      toast.error(t("estudante.notify.error"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className='modal fade' id={modalId} tabIndex={-1} aria-hidden='true' ref={modalRef}>
      <div className='modal-dialog modal-dialog-centered modal-lg'>
        <div className='modal-content radius-16 bg-base'>
          <div className='modal-header py-16 px-24 border border-top-0 border-start-0 border-end-0'>
            <h6 className='modal-title mb-0'>
              {t("estudante.notify.title.count", { count: count ?? pairs?.length ?? 0 })}
            </h6>
            <button type='button' className='btn-close' data-bs-dismiss='modal' aria-label='Close' />
          </div>
          <div className='modal-body p-24'>
            <div className='mb-16'>
              <label className='form-label'>{t("estudante.notify.type")}</label>
              <div className='d-flex gap-24'>
                <div className='form-check'>
                  <input
                    id={`${modalId}-type-convocatoria`}
                    type='radio'
                    className='form-check-input'
                    name={`${modalId}-type`}
                    value='Convocatoria'
                    checked={fields.type === "Convocatoria"}
                    onChange={setField("type")}
                  />
                  <label className='form-check-label' htmlFor={`${modalId}-type-convocatoria`}>
                    {t("estudante.notify.type.convocatoria")}
                  </label>
                </div>
                <div className='form-check'>
                  <input
                    id={`${modalId}-type-comunicado`}
                    type='radio'
                    className='form-check-input'
                    name={`${modalId}-type`}
                    value='Comunicado'
                    checked={fields.type === "Comunicado"}
                    onChange={setField("type")}
                  />
                  <label className='form-check-label' htmlFor={`${modalId}-type-comunicado`}>
                    {t("estudante.notify.type.comunicado")}
                  </label>
                </div>
              </div>
            </div>

            {fields.type === "Convocatoria" && (
              <div className='row gy-3 mb-16'>
                <div className='col-sm-6'>
                  <label className='form-label'>{t("estudante.notify.meetingDate")} <span className='required-asterisk'>*</span></label>
                  <div className="icon-field">
                    <span className="icon">
                      <Icon icon="solar:calendar-outline" />
                    </span>
                    <input
                      type='date'
                      className='form-control'
                      value={fields.meetingDate}
                      onChange={setField("meetingDate")}
                    />
                  </div>
                </div>
                <div className='col-sm-6'>
                  <label className='form-label'>{t("estudante.notify.meetingTime")} <span className='required-asterisk'>*</span></label>
                  <div className="icon-field">
                    <span className="icon">
                      <Icon icon="solar:clock-circle-outline" />
                    </span>
                    <input
                      type='time'
                      className='form-control'
                      value={fields.meetingTime}
                      onChange={setField("meetingTime")}
                    />
                  </div>
                </div>
                <div className='col-sm-6'>
                  <label className='form-label'>{t("estudante.notify.agenda1")} <span className='required-asterisk'>*</span></label>
                  <div className="icon-field">
                    <span className="icon">
                      <Icon icon="solar:document-text-outline" />
                    </span>
                    <input
                      type='text'
                      maxLength={120}
                      className='form-control'
                      placeholder={t("estudante.notify.agenda1.placeholder")}
                      value={fields.agenda1}
                      onChange={setField("agenda1")}
                    />
                  </div>
                </div>
                <div className='col-sm-6'>
                  <label className='form-label'>{t("estudante.notify.agenda2")}</label>
                  <div className="icon-field">
                    <span className="icon">
                      <Icon icon="solar:document-text-outline" />
                    </span>
                    <input
                      type='text'
                      maxLength={120}
                      className='form-control'
                      placeholder={t("estudante.notify.agenda2.placeholder")}
                      value={fields.agenda2}
                      onChange={setField("agenda2")}
                    />
                  </div>
                </div>
                <div className='col-sm-6'>
                  <label className='form-label'>{t("estudante.notify.agenda3")}</label>
                  <div className="icon-field">
                    <span className="icon">
                      <Icon icon="solar:document-text-outline" />
                    </span>
                    <input
                      type='text'
                      maxLength={120}
                      className='form-control'
                      placeholder={t("estudante.notify.agenda3.placeholder")}
                      value={fields.agenda3}
                      onChange={setField("agenda3")}
                    />
                  </div>
                </div>
                <div className='col-sm-6'>
                  <label className='form-label'>{t("estudante.notify.agenda4")}</label>
                  <div className="icon-field">
                    <span className="icon">
                      <Icon icon="solar:document-text-outline" />
                    </span>
                    <input
                      type='text'
                      maxLength={120}
                      className='form-control'
                      placeholder={t("estudante.notify.agenda4.placeholder")}
                      value={fields.agenda4}
                      onChange={setField("agenda4")}
                    />
                  </div>
                </div>
                <div className='col-12'>
                  <label className='form-label'>{t("estudante.notify.notes")}</label>
                  <textarea
                    rows={2}
                    maxLength={250}
                    className='form-control'
                    value={fields.notes}
                    onChange={setField("notes")}
                  />
                </div>
              </div>
            )}

            {fields.type === "Comunicado" && (
              <div className='mb-16'>
                <label className='form-label'>{t("estudante.notify.message")} <span className='required-asterisk'>*</span></label>
                <textarea
                  rows={4}
                  maxLength={500}
                  className='form-control'
                  value={fields.comunicado}
                  onChange={setField("comunicado")}
                  placeholder={t("estudante.notify.message.placeholder")}
                />
              </div>
            )}

            <div>
              <label className='form-label'>{t("estudante.notify.preview")}</label>
              <textarea rows={6} className='form-control' value={preview} readOnly />
            </div>
          </div>
          <div className='modal-footer border border-bottom-0 border-start-0 border-end-0'>
            <button type='button' className='btn btn-neutral-500 border-neutral-100 px-24' data-bs-dismiss='modal'>
              {t("global.cancel")}
            </button>
            <button
              type='button'
              className='btn btn-primary-600 px-24 d-flex align-items-center gap-2'
              onClick={handleConfirm}
              disabled={sending}
            >
              {sending && <Icon icon='svg-spinners:ring-resize' />}
              {t("global.confirm")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotifyModal;
